// @givmo/design-system — SearchInput (SaaS layer).
//
// The single shared search control — a leading magnifier (left-aligned), SaaS tokens (8px radius,
// border-strong idle, shadow-ring focus, text-placeholder), and an optional clear (×) button shown
// when there's a value. Used by every table toolbar.
//
// (`IconButton` moved to `./icon-button` in R3 — re-exported here for back-compat with existing
// `import { IconButton } from './search-input'` consumers.)
import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';

export { IconButton, iconButtonVariants, type IconButtonProps } from './icon-button';

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, TestIdProps {
  /** Width utility class override (defaults to a comfortable fixed width). */
  containerClassName?: string;
  /** Show a clear (×) button + invoke this when clicked (only renders when `value` is non-empty). */
  onClear?: () => void;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { className, containerClassName, testId, placeholder = 'Search…', onClear, value, ...props },
  ref,
) {
  const showClear = onClear != null && typeof value === 'string' && value.length > 0;
  return (
    <div className={cn('relative w-[280px] max-w-full', containerClassName)}>
      <span className="pointer-events-none absolute inset-y-0 left-10 flex items-center text-icon-muted">
        <Icon icon={Search} size={16} aria-hidden />
      </span>
      <input
        ref={ref}
        type="search"
        value={value}
        placeholder={placeholder}
        className={cn(
          // The input is white-on-white against a card, so its border is the only thing that
          // identifies the control — a control boundary needing ≥3:1 (WCAG 1.4.11). neutral-500 =
          // 4.68:1 on white / 4.47:1 on surface-sunken; the border-strong it replaced was 1.48:1.
          // Same token as the unchecked Checkbox box, so every input-edge reads alike.
          'h-36 w-full rounded-md border border-neutral-500 bg-surface pl-32',
          showClear ? 'pr-32' : 'pr-12',
          'font-sans text-s14 font-w400 tracking-givmo text-text placeholder:text-text-placeholder',
          'outline-none transition-[box-shadow,border-color]',
          // Hover darkens rather than lightens (neutral-600 = 7.54:1); the old neutral-400 dropped
          // the boundary to 2.52:1 on hover.
          'hover:border-neutral-600',
          'focus-visible:border-brand-500 focus-visible:shadow-ring',
          '[&::-webkit-search-cancel-button]:appearance-none',
          className,
        )}
        {...testIdProp(testId)}
        {...props}
      />
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear search"
          className="absolute inset-y-0 right-8 flex items-center text-icon-muted outline-none transition-colors hover:text-text-secondary focus-visible:text-text-secondary"
          {...testIdProp(testId ? `${testId}-clear` : undefined)}
        >
          <Icon icon={X} size={15} aria-hidden />
        </button>
      )}
    </div>
  );
});
